import { Col, Row } from 'react-bootstrap';
import { useCallback, useMemo, useState } from 'react';
import { Error } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';

import CameraModal from '../camera-modal';
import { uuid } from '../../../../types/common-helper';
import { dataURItoBlob } from '../../../../utils/image-helper';
import ImageViewWithFullScreen from '../../../image-view-with-fullscreen';

const generateRandomString = () => {
  return uuid().split('-').join('').substring(0, 5);
};

const AnswerImage: React.FC = () => {
  const [blobImage, setBlobImage] = useState<string>('');
  const [isDisplayWebcam, setIsDisplayWebcam] = useState<boolean>(false);
  const [fileUpload, setFileUpload] = useState<File>();
  const [imageExtension, setImageExtension] = useState<string>('');
  const [isValidFileExtension, setIsValidFileExtension] =
    useState<boolean>(true);
  const [facingMode, setFacingMode] = useState<string>('environment');

  const turnOnCameraClick = () => {
    setIsDisplayWebcam(true);
    setIsValidFileExtension(true);
  };

  const handleCapture = useCallback((screenShot: string) => {
    fetch(screenShot)
      .then((result) => {
        const imageFile = dataURItoBlob(result.url);
        let myFile = imageFile;
        if (imageFile instanceof Blob) {
          const extension = imageFile.type.split('/')[1];
          setImageExtension(extension);
          myFile = new File(
            [imageFile],
            `captured-image-${generateRandomString()}.${extension}`,
            {
              type: imageFile.type
            }
          );
        }

        setBlobImage(result.url);
      })
      .catch((error) => console.error(error))
      .finally(() => setIsDisplayWebcam(false));
  }, []);

  const getFileExtension = (filename: string) => {
    // get file extension
    const extension = filename.substring(
      filename.lastIndexOf('.') + 1,
      filename.length
    );
    return extension;
  };

  const allowedImages = useMemo(
    () =>
      getFileExtension(imageExtension).toLocaleLowerCase() === 'png' ||
      getFileExtension(imageExtension).toLocaleLowerCase() === 'jpg' ||
      getFileExtension(imageExtension).toLocaleLowerCase() === 'jpeg' ||
      getFileExtension(imageExtension).toLocaleLowerCase() === 'gif' ||
      getFileExtension(imageExtension).toLocaleLowerCase() === 'heic' ||
      getFileExtension(imageExtension) === '',
    [imageExtension]
  );

  const handleSwitchCamera = useCallback(() => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  }, [setFacingMode]);

  return (
    <div id={'answer-image'} className={'d-flex flex-column flex-grow-1 h-100'}>
      <div
        className={
          'd-flex flex-column justify-content-center border text-center position-relative'
        }
        style={{
          flex: 1,
          overflow: 'hidden'
        }}>
        <div
          className={'d-flex justify-content-center align-items-center h-100'}>
          {blobImage === '' ? (
            <div
              className={
                'd-flex flex-column align-items-center justify-content-center'
              }>
              <span
                className={'material-symbols-outlined text-success'}
                style={{
                  fontSize: '64px'
                }}>
                wallpaper
              </span>
              <span>Preview</span>
            </div>
          ) : allowedImages ? (
            <ImageViewWithFullScreen src={blobImage ?? ''} alt={'preview'}>
              <div className="image-preview-container">
                <img
                  src={blobImage ?? ''}
                  alt={'Preview'}
                  className="preview-image"
                />
              </div>
            </ImageViewWithFullScreen>
          ) : (
            <p>
              <span
                className={'material-symbols-outlined text-primary'}
                style={{
                  fontSize: '64px'
                }}>
                cloud_done
              </span>
              <br />
              {fileUpload?.name ??
                decodeURI(blobImage.substring(blobImage.lastIndexOf('/') + 1))}
            </p>
          )}
        </div>
      </div>

      <Row className={'py-2'}>
        {!isValidFileExtension && (
          <Col className={'mb-4'} md={12}>
            <Error>Invalid File Type: Please upload image files only.</Error>
          </Col>
        )}

        <div className={'d-flex flex-column gap-2'}>
          <Button
            className={'w-100 upload-button p-2'}
            onClick={turnOnCameraClick}>
            <div
              className={
                'd-flex justify-content-center align-items-center gap-3'
              }>
              <span
                className={'material-symbols-outlined mr-2 text-success'}
                role={'button'}>
                add_a_photo
              </span>
              <span className={'fw-normal'}>Take photo</span>
            </div>
          </Button>
        </div>
      </Row>

      <CameraModal
        isOpen={isDisplayWebcam}
        onClose={() => setIsDisplayWebcam(false)}
        onCapture={handleCapture}
        facingMode={facingMode}
        onSwitchCamera={handleSwitchCamera}
      />
    </div>
  );
};

export default AnswerImage;
